/**
 * Modal re-authentication dialog.
 *
 * Rendered by AuthProvider OVER the current page when a silent token refresh
 * fails. The page underneath is never unmounted and no route changes, so
 * in-progress form state survives; on success the parked requests replay and
 * the user continues exactly where they were.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface SessionReauthDialogProps {
  open: boolean;
  email: string;
  onSubmit: (password: string) => Promise<void>;
  onSignOut: () => void;
}

const SessionReauthDialog: React.FC<SessionReauthDialogProps> = ({
  open,
  email,
  onSubmit,
  onSignOut,
}) => {
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(password);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Sign-in failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth aria-labelledby="reauth-dialog-title">
      <form onSubmit={handleSubmit}>
        <DialogTitle id="reauth-dialog-title">Session expired</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 2 }}>
            Confirm your password to continue — your work on this page is untouched.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            margin="dense"
            fullWidth
            label="Email"
            value={email}
            disabled
          />
          <TextField
            margin="dense"
            fullWidth
            autoFocus
            required
            name="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {/* Disabled mid-submit: signing out while a re-auth is in flight
              would leave the freshly minted session live but unreachable. */}
          <Button onClick={onSignOut} color="inherit" disabled={submitting}>
            Sign out
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || !password}>
            {submitting ? 'Signing in…' : 'Continue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SessionReauthDialog;
